/**
 * One shared label module for analyser progress — wizard and modal.
 * Plain ASD-STE100 words. No playful copy. No raw phase ids in the UI.
 */

export type AnalyseProgressLike = {
  phase?: string | null;
  done?: number | null;
  total?: number | null;
  current?: string | null;
};

const PHASE_FALLBACK: Record<string, string> = {
  starting: "Starting",
  schema: "Reading the tabs",
  persisting: "Saving the columns",
  sampling: "Reading sample rows",
  done: "Done",
};

/**
 * Primary progress line. Prefer a concrete tab when the API names one.
 * Examples: "Reading tab 2 of 5: Orders", "Reading sample rows",
 * "Saving the columns", "Building your panel".
 */
export function analysePhaseLabel(
  progress: AnalyseProgressLike | string | null | undefined,
): string {
  if (progress == null) return "Starting";
  if (typeof progress === "string") {
    return PHASE_FALLBACK[progress] ?? "Analyzing";
  }

  const phase = progress.phase ?? "";
  const done = progress.done ?? 0;
  const total = progress.total ?? 0;
  const current = (progress.current ?? "").trim();

  if (phase === "done") return "Done";
  if (phase === "persisting") return "Saving the columns";
  if (phase === "sampling") {
    return current ? `Reading sample rows: ${current}` : "Reading sample rows";
  }
  if (phase === "schema" || phase === "starting") {
    // `done` is completed count; show 1-based current tab, capped at total.
    const tabN = total > 0 ? Math.min(done + 1, total) : Math.max(done, 1);
    if (total > 0 && current) {
      return `Reading tab ${tabN} of ${total}: ${current}`;
    }
    if (total > 0) {
      return `Reading tab ${tabN} of ${total}`;
    }
    if (current) return `Reading ${current}`;
    return phase === "starting" ? "Starting" : "Reading the tabs";
  }
  if (phase === "building" || phase === "panel") {
    return "Building your panel";
  }
  return PHASE_FALLBACK[phase] ?? "Building your panel";
}

/** Elapsed time for a muted second line. */
export function analyseElapsedLabel(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const r = seconds % 60;
  return `${m}m ${r.toString().padStart(2, "0")}s`;
}

/** Which tab name is currently being read, if the progress names one. */
export function analyseCurrentTab(
  progress: AnalyseProgressLike | null | undefined,
): string | null {
  const current = progress?.current?.trim();
  return current || null;
}
